import React from 'react';
import ClosableDialogTitleBar from './ClosableDialogTitleBar';
import { DialogContent, Dialog, Theme } from '@mui/material';
import { buttonStyles, dividerStyles } from '../../../config/Theme';
import { Nullable } from '../../../models/common/Nullable';
import {makeStyles} from "@mui/styles";
import {TextDisplay} from "../data-display/TextDisplay";
import {UnsavedDataProtection} from "./UnsavedDataProtection";
import {useVisibility} from "../../../config/hooks/useToggle";
import {VisibilityHandler} from "../VisibilityHandler";

/**
 * Represents the configuration properties for a dialog component with close functionality.
 * This interface is used to control the behavior, content, and appearance of a closable dialog.
 */
export interface ClosableDialogProps {
    onCloseClick: () => void
    isOpen: boolean
    title: string
    fullWidth?: boolean
    children: React.ReactNode
    dialogActionComponents?: Nullable<React.ReactNode>
    className?: string
    dialogBoxClassName?: string
    contentClassName?: string
    contentMinHeight?: number
    style?: React.CSSProperties
    enableCloseConfirmation?: boolean
}

/**
 * Generates a set of reusable styles for a component.
 *
 * This variable exists to centralize styling for buttons, dividers, and specific component elements like content and titles.
 * It helps maintain consistency in design and enables easier updates when the theme changes.
 *
 * The `useStyles` styling object merges predefined button and divider styles with additional, component-specific customizations.
 *
 **/
const useStyles = makeStyles(( theme: Theme ) => ({
        ...buttonStyles(theme),
        ...dividerStyles(theme),
        content: {
            padding: theme.spacing(),
        },
        title: {
            color: theme.palette.common.white,
            lineHeight: '48px',

        }
    })
);


/**
 * A customizable dialog component designed for user interactions where a modal window is required.
 * Supports enhanced functionality like close confirmation, flexible content, and configurable styles.
 *
 * This component is useful in scenarios where you need a modal dialog with optional actions and a close confirmation prompt to prevent unintended closures.
 *
 * @param title The title of the dialog. This text is displayed prominently at the top of the dialog.
 * @param onCloseClick Callback function triggered when the dialog’s close action is initiated.
 * If `enableCloseConfirmation` is true, the close operation will require confirmation.
 * @param isOpen Boolean that determines whether the dialog is visible or hidden.
 * @param children React children that represent the main content to display inside the dialog.
 * @param dialogActionComponents Optional ReactNode element(s) representing actions (e.g., buttons) displayed in the dialog footer.
 * @param className CSS class applied to the overall dialog for styling customization.
 * @param contentClassName CSS class applied specifically to the dialog content area for custom styling.
 * @param dialogBoxClassName CSS class applied to the dialog box for styling.
 * @param fullWidth Boolean that determines whether the dialog should stretch to the full width for larger content.
 * @param style Inline style object applied to the dialog for additional CSS overrides.
 * @param contentMinHeight Defines the minimum height (in pixels or other units) for the main content area of the dialog. Defaults to "400px" if not provided.
 * @param enableCloseConfirmation Boolean flag to enable or disable confirmation before closing the dialog. If true, a secondary confirmation dialog will appear.
 *
 * @return A React component rendering a dialog with a title bar, main content area, optional actions, and an optional close confirmation mechanism.
 *
 * @throws Error If required parameters (e.g., `title` or `onCloseClick`) are missing or improperly passed.
 */
function ClosableDialog({
         title,
         onCloseClick,
         isOpen,
         children,
         dialogActionComponents,
         className,
         contentClassName,
        dialogBoxClassName,
        fullWidth,
        style,
    contentMinHeight,
    enableCloseConfirmation
    }: ClosableDialogProps
) {
    const [ closeConfirmDialogVisible, { toggleVisibility: toggleCloseConfirmDialogVisibility } ] = useVisibility( false );
    const classes = useStyles();
    const actionComponents = dialogActionComponents || null;

    const handleCancelClick = ( ) => {

        if( enableCloseConfirmation ) {
            toggleCloseConfirmDialogVisibility();
        } else {
            onCloseClick();
        }
    };



    return (
        <div>
            <Dialog
                disableEscapeKeyDown
                className={ className || ''  }
                open={ isOpen }
                classes={{ paper: ( fullWidth ? ' dialog-full-width' : '' )+ ' ' }}
                onClose={ handleCancelClick }
                fullWidth={ fullWidth }
                maxWidth={ fullWidth ? "xl" : undefined}
                style={style}
            >
                <div className={ dialogBoxClassName || '' } style={ { width: "inherit", minHeight: contentMinHeight || "400px" }}>
                    <div>
                        <ClosableDialogTitleBar
                            onClose={ handleCancelClick }
                        >
                            <TextDisplay variant="h5" className={classes.title}>
                                { title }
                            </TextDisplay>
                        </ClosableDialogTitleBar>
                    </div>
                    <div style={{ padding: "15px" }}>
                        <DialogContent className={ contentClassName || classes.content }>
                            {children}
                        </DialogContent>
                    </div>

                </div>

                    <div className="flex justify-end padding-10">
                        { actionComponents }
                    </div>
            </Dialog>

            <VisibilityHandler isVisible={ enableCloseConfirmation || false }>
                <UnsavedDataProtection
                    isOpen={ closeConfirmDialogVisible }
                    onYesClick={ () => {
                        toggleCloseConfirmDialogVisibility();
                        onCloseClick();
                    }}
                    onNoClick={ toggleCloseConfirmDialogVisibility }
                />
            </VisibilityHandler>

        </div>
    );
}

export default ClosableDialog;